import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useMemo, useRef } from 'react';
import {
  addAsyncResponse,
  addAsyncResponseChunk,
  addSyncMessageToActiveChat,
  clearSearch,
  createGhostChat as createGhostChatAction,
  deleteChat as deleteChatAction,
  deleteTempChat,
  finishAsyncResponse,
  replaceTempWithChatIdAndName,
  resetActiveAgent as resetActiveAgentAction,
  resetAnimateName,
  selectActiveAgentId,
  selectActiveChatId,
  selectChatsDetails,
  selectChatsList,
  setActiveAgent as setActiveAgentAction,
  setActiveChat,
  setChat,
  setChatIsFavorite,
  setChatName,
  setChatsList,
  setLoadingChatDetails,
  setLoadingChatListItem,
  setLoadingChatsList,
  setLoadingSearch,
  setMessageAnimationProgress,
  setSearchResult,
  setTempChatId as setTempChatIdAction,
  stopResponse as stopResponseAction,
  TEMP_CHAT_ID,
} from './agentsChatsReducer';
import { sortByDate } from '../../utils/sortByDate';
import { SortOrder } from '../common/types';
import { useAiAgentApiFacade } from '../../apis/facades/useAiAgentApiFacade';
import { selectDefaultCreationCrmOrgId, selectUserAccountId } from '../../reducers/userInfoReducer';
import { DateTime } from 'luxon';
import { AgentType, ChatCompletionRequestMessageRole } from '@server/ai';
import { telemetry } from '../../telemetry';
import { selectAgents } from './agentsSelectors';
import { AI_AGENTS_EVENTS } from '../../services/events';
import useSendBiEvent from '../../hooks/useSendBiEvent';
import { AiAgent } from './aiAgentsConsts';
import { useLocation } from 'react-router';
import { appRoutes } from '../../constants/appRoutes';
import useSweepNavigate from '../../navigation-utils/useSweepNavigate';
import { useChatSubscriptions } from './context/ChatSubscriptionContext';
import { AiChatMessageChunk } from '../common/ai-chat/aiChatTypes';
import { useAgentChatInstance } from './AgentChatInstanceContext';
import { useHandleDreamforceDemo } from '../../dreamforce-demo/hooks/useHandleDreamforceDemo';
import { generateAgentChatUrl } from './helper';
import { AgentInputMode } from './agent-input/agentInputModeTypes';
import {
  selectAdvancedMode,
  setAdvancedMode,
  selectSelectedAdditionalOrgIds,
  setSelectedAdditionalOrgIds,
  setArtifactsViewActive,
  resetActiveFakeAgent,
} from './agentsHomeReducer';
import { useFeatureToggle } from '../../feature-flags/useFeatureToggle';
import { selectConnectedCrmOrgs } from '../pages/environments/environmentsReducer';
import useAgentsHome from './useAgentsHome';
import useUserInfo from '../../hooks/useUserInfo';

const useAgentsChats = () => {
  const location = useLocation();
  const isPageView = !location.pathname.startsWith(appRoutes.funnelEditor.route);
  const { chatInstanceId } = useAgentChatInstance();
  // Track which chats are currently being handled for errors to prevent duplicate error messages
  const errorHandlingChats = useRef<Set<string>>(new Set());
  const userAccountId = useSelector(selectUserAccountId);
  const { handleDemoCommand } = useHandleDreamforceDemo();
  const {
    post_newChat,
    get_chats,
    patch_sendMessage,
    get_chat,
    rename_chat,
    delete_chat,
    set_isFavorite_chat,
    get_search,
    delete_stream,
  } = useAiAgentApiFacade();
  const dispatch = useDispatch();
  const sendBiEvent = useSendBiEvent();
  const { navigate } = useSweepNavigate();
  const { updateHubspotContact } = useUserInfo();
  const activeAgentId = useSelector(selectActiveAgentId);
  const chatsDetails = useSelector(selectChatsDetails);
  const activeChatId = useSelector(selectActiveChatId(chatInstanceId));
  const chatsList = useSelector(selectChatsList);

  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const _crmOrgId = crmOrgId ?? undefined;
  const agents = useSelector(selectAgents(_crmOrgId));
  //memo, because active agent makes other components recalculate (and components unmount and mount again eg. prompts library dialog)
  //.find always returns a new object reference, even when the agent data is identical
  const activeAgent = useMemo(
    () => agents?.find((agent) => agent.id === activeAgentId),
    [agents, activeAgentId],
  );
  const activeAgentType = activeAgent?.type;
  const activeChat = activeChatId ? chatsDetails?.[activeChatId] : undefined;

  const { subscribeToChat, unsubscribeFromChat } = useChatSubscriptions();
  const { closeIssuesPanel } = useAgentsHome();

  const handleMessageChunk = useCallback(
    (chatId: string, chunk: AiChatMessageChunk, isFirstContentChunk: boolean, agentId?: string) => {
      const _agentId = agentId ?? activeAgentId;
      if (!_agentId) {
        return;
      }

      // Mark that this chat has a working stream - clear any error states
      errorHandlingChats.current.add(chatId + '_resolved');
      errorHandlingChats.current.delete(chatId); // Clear any pending/active error handling

      dispatch(
        addAsyncResponseChunk({
          agentId: _agentId,
          chatId,
          chunk: {
            content: chunk.content ?? '',
            reasoning: chunk.reasoning,
            createdAt: DateTime.now().toISO() ?? '',
            role: ChatCompletionRequestMessageRole.ASSISTANT,
            isError: chunk.isError,
            artifactMetadata: chunk.artifactMetadata,
            artifactContent: chunk.artifactContent,
            tableMetadata: chunk.tableMetadata,
            runId: chunk.runId,
          },
          isFirstContentChunk,
          chatInstanceId,
        }),
      );
    },
    [activeAgentId, chatInstanceId, dispatch],
  );

  const handleStreamComplete = useCallback(
    (chatId: string, agentId?: string) => {
      const _agentId = agentId ?? activeAgentId;
      if (_agentId) {
        dispatch(finishAsyncResponse({ agentId: _agentId, chatId, chatInstanceId }));
      }
    },
    [activeAgentId, dispatch, chatInstanceId],
  );

  const handleStreamError = useCallback(
    async (chatId: string, agentId?: string, agentType?: AgentType) => {
      const _agentId = agentId ?? activeAgentId;
      const _agentType = agentType ?? AgentType.Documentation;

      // Prevent duplicate error handling for the same chat
      if (errorHandlingChats.current.has(chatId)) {
        return;
      }

      // Check if a working stream has already resolved this chat
      if (errorHandlingChats.current.has(chatId + '_resolved')) {
        return;
      }

      // For big payloads, check if backend created a new stream before showing error
      // This handles the case where first stream times out but second stream becomes available
      try {
        const chat = await get_chat(chatId);
        if (chat.hasStream) {
          // New stream is available, Update loading state and subscribe
          if (_agentId) {
            dispatch(
              setChat({
                agentId: _agentId,
                chat: {
                  ...chat,
                  agentType: _agentType,
                  isLoadingResponse: true, // Reset loading state for new stream
                  isStreamingResponse: false,
                },
                chatInstanceId,
              }),
            );
          }

          // Subscribe to the new stream
          const isSubscribed = await subscribeToChat(chatId, {
            onMessage: (chatId, chunk, isFirstContentChunk) =>
              handleMessageChunk(chatId, chunk, isFirstContentChunk, _agentId),
            onComplete: (chatId) => handleStreamComplete(chatId, _agentId),
            onError: (chatId) => handleStreamError(chatId, _agentId, _agentType),
          });
          if (isSubscribed) {
            return; // Successfully connected to new stream - don't show error
          }
        }
      } catch (err) {
        // Failed to check for new stream, continue with error handling
      }

      // Wait a bit to avoid showing errors immediately for transient issues
      await new Promise((resolve) => setTimeout(resolve, 5000));

      // Check if a working stream resolved this while we were waiting
      if (errorHandlingChats.current.has(chatId + '_resolved')) {
        return; // Working stream came through - don't show error
      }

      errorHandlingChats.current.add(chatId);

      // If stream failed to arrive, but the response exists in the DB, we just need to render it
      // E.g1 User enters an existing chat, gets "hasStream:true", but the stream is closed before "subscribeToChatResponse" was called
      // E.g2 Stream timed-out
      try {
        const chat = await get_chat(chatId);
        const lastMessageInDb = chat.aiChatDetails.messages[chat.aiChatDetails.messages.length - 1];
        const isLastMessageByAssistant =
          lastMessageInDb.role === ChatCompletionRequestMessageRole.ASSISTANT;

        if (isLastMessageByAssistant && _agentId) {
          dispatch(
            addAsyncResponse({
              chatId,
              message: lastMessageInDb,
              agentId: _agentId,
              chatInstanceId,
            }),
          );
          errorHandlingChats.current.delete(chatId); // Clear error handling flag on success
          return;
        }
      } catch (e) {
        telemetry.captureError(e);
      }

      // Missing server response due to other edge cases - need to investigate and decide what to do
      telemetry.captureError(new Error('Unknown chat error'));

      if (_agentId) {
        dispatch(
          addAsyncResponse({
            chatId,
            message: {
              content: 'Connection error. Please try again later.',
              createdAt: DateTime.now().toISO() ?? '',
              role: ChatCompletionRequestMessageRole.ASSISTANT,
              isError: true,
            },
            agentId: _agentId,
            chatInstanceId,
          }),
        );
      }

      // Always clear error handling flag when done (success or failure)
      errorHandlingChats.current.delete(chatId);
    },
    [
      activeAgentId,
      chatInstanceId,
      dispatch,
      get_chat,
      subscribeToChat,
      handleMessageChunk,
      handleStreamComplete,
    ],
  );

  const fetchChats = useCallback(async () => {
    if (!activeAgentId) return;
    if (chatsList?.isLoadingChatList || chatsList?.data) {
      return;
    }
    try {
      dispatch(setLoadingChatsList({ agentId: activeAgentId, chatInstanceId }));
      const chats = await get_chats({ agentId: activeAgentId });
      dispatch(setChatsList({ agentId: activeAgentId, chats, chatInstanceId }));
    } catch (e) {
      telemetry.captureError(e);
    }
  }, [activeAgentId, dispatch, get_chats, chatInstanceId, chatsList]);

  const orderedChats = useMemo(
    () =>
      chatsList?.data?.toSorted((a, b) => sortByDate(a.updatedAt, b.updatedAt, SortOrder.DESC)) ??
      [],
    [chatsList],
  );

  const navigateToAgentOrChat = useCallback(
    ({ agentId, chatId }: { agentId?: string; chatId?: string }) => {
      const destination = generateAgentChatUrl({
        accountId: userAccountId ?? '',
        agentId,
        chatId,
        crmOrgId: _crmOrgId,
      });
      navigate(destination);
    },
    [_crmOrgId, navigate, userAccountId],
  );

  const setTempChatAsActive = useCallback(
    ({ agentId }: { agentId: string }) => {
      dispatch(
        setTempChatIdAction({
          agentId,
          chatInstanceId,
        }),
      );
    },
    [dispatch, chatInstanceId],
  );

  const navigateIfCurrentIsTemp = useCallback(
    (agentId: string, chatId: string) => {
      // Check if user is still on the temp chat URL
      // To avoids closure issues we directly check the current location
      const currentLocationPathname = window.location.pathname;
      const isTempChatInUrl = currentLocationPathname.includes(TEMP_CHAT_ID);

      if (isTempChatInUrl) {
        navigateToAgentOrChat({ agentId, chatId });
      }
    },
    [navigateToAgentOrChat],
  );

  const onConfirmMessage = useCallback(
    async ({
      messageStr,
      agentId,
      agentType,
      replyTo,
      isFirstMessage,
      disableNavigation,
      dontSendToServer,
      advancedMode,
      additionalOrgIds,
    }: {
      messageStr: string;
      agentId: string;
      agentType: AgentType;
      replyTo?: string;
      isFirstMessage?: boolean;
      disableNavigation?: boolean;
      dontSendToServer?: boolean;
      advancedMode?: AgentInputMode;
      additionalOrgIds?: string[];
    }) => {
      // 🎯 ONLY FOR DREAMFORCE DEMO - to be deleted
      await handleDemoCommand(messageStr);

      sendBiEvent({
        name: AI_AGENTS_EVENTS.newQuestion,
      });

      updateHubspotContact({ last_ai_chat_date: Date.now() });

      let newChatIdToUse: string | undefined = undefined;
      const createdAt = DateTime.now().toISO();
      const newMessage = {
        role: ChatCompletionRequestMessageRole.USER,
        content: messageStr,
        createdAt,
        ...(replyTo ? { replyTo } : {}),
      };

      dispatch(
        addSyncMessageToActiveChat({
          message: newMessage,
          agentId,
          agentType,
          isNewChat: isFirstMessage,
          chatInstanceId,
          isLoadingResponse: !dontSendToServer,
        }),
      );

      try {
        if (isFirstMessage) {
          sendBiEvent({
            name: AI_AGENTS_EVENTS.createNewChat,
            props: { agentType },
          });

          //go directly to the chat UI, while using a temp id
          if (isPageView && !disableNavigation) {
            navigateToAgentOrChat({
              agentId: activeAgentId,
              chatId: TEMP_CHAT_ID,
            });
          } else {
            setTempChatAsActive({ agentId });
          }

          if (!dontSendToServer) {
            const res = await post_newChat(
              agentId,
              { message: newMessage },
              { advancedMode, additionalOrgIds },
            );
            newChatIdToUse = res.id;
            dispatch(
              replaceTempWithChatIdAndName({
                chatId: newChatIdToUse,
                name: res.name,
                agentId,
                agentType,
                chatInstanceId,
              }),
            );

            // Can't use the custom "useTimeout" here because the consuming component ("AgentEmptyState") unmounts before the callback is fired.
            // Safe to use setTimeout without cleanup: 0ms delay + only primitive captures + Redux dispatch
            setTimeout(() => {
              //allow the name animation to run only once, and then reset the property
              dispatch(
                resetAnimateName({
                  agentId,
                  chatId: newChatIdToUse ?? '',
                }),
              );
            }, 1000);
          }

          // Handle chat activation:
          const shouldNavigate = isPageView && !disableNavigation;
          if (shouldNavigate && newChatIdToUse) {
            // Navigation will handle setActiveChat through route change (in useAgentRouteSync)
            navigateIfCurrentIsTemp(agentId, newChatIdToUse);
          } else {
            // No navigation, so explicitly set the active chat
            if (newChatIdToUse) {
              dispatch(setActiveChat({ chatId: newChatIdToUse, agentId, chatInstanceId }));
            }
          }
        } else {
          // the "else" handles sending adding a message to an existing chat (patch)
          if (!dontSendToServer) {
            await patch_sendMessage(
              activeChatId ?? '',
              { message: newMessage },
              { advancedMode, additionalOrgIds },
            );
          }
        }
      } catch (e) {
        dispatch(
          addAsyncResponse({
            chatId: newChatIdToUse ?? activeChatId,
            message: {
              content: 'Something went wrong, please try again later',
              createdAt: DateTime.now().toISO() ?? '',
              role: ChatCompletionRequestMessageRole.ASSISTANT,
              isError: true,
            },
            agentId,
            chatInstanceId,
          }),
        );
        telemetry.captureError(e);
      }

      if (!dontSendToServer) {
        const _chatId = newChatIdToUse ?? activeChatId;
        if (_chatId) {
          const isSubscribed = await subscribeToChat(_chatId, {
            onMessage: (chatId, chunk, isFirstContentChunk) =>
              handleMessageChunk(chatId, chunk, isFirstContentChunk, agentId),
            onComplete: (chatId) => handleStreamComplete(chatId, agentId),
            onError: (chatId) => handleStreamError(chatId, agentId, agentType),
          });
          if (!isSubscribed) {
            handleStreamError(_chatId, agentId, agentType);
          }
        }
      }
    },
    [
      dispatch,
      chatInstanceId,
      activeAgentId,
      activeChatId,
      sendBiEvent,
      isPageView,
      post_newChat,
      navigateToAgentOrChat,
      setTempChatAsActive,
      navigateIfCurrentIsTemp,
      patch_sendMessage,
      subscribeToChat,
      handleDemoCommand,
      handleMessageChunk,
      handleStreamComplete,
      handleStreamError,
      updateHubspotContact,
    ],
  );

  const setActiveAgent = useCallback(
    (agent: AiAgent) => {
      const { id, type } = agent;
      dispatch(setActiveAgentAction({ agentId: id, agentType: type, chatInstanceId }));
    },
    [dispatch, chatInstanceId],
  );

  const setAndGetActiveChat = useCallback(
    async ({ agent, chatId }: { agent: AiAgent; chatId?: string }) => {
      if (!activeAgentId) {
        setActiveAgent(agent);
      }
      if (!chatId) {
        navigateToAgentOrChat({ agentId: agent.id });
        return;
      }
      if (chatsDetails?.[chatId]) {
        dispatch(setActiveChat({ agentId: agent.id, chatId, chatInstanceId }));
        if (activeChatId === TEMP_CHAT_ID) {
          dispatch(
            deleteTempChat({
              agentId: agent.id,
            }),
          );
        }
        return;
      }

      try {
        dispatch(setLoadingChatDetails({ agentId: agent.id, chatId, chatInstanceId }));

        const chat = await get_chat(chatId);

        // Check if this chat is in the current chat list - if not, it's a shared/external chat
        const isInChatList = chatsList?.data?.some((c) => c.id === chat.id) ?? false;
        const isReadOnly = !isInChatList;

        dispatch(
          setChat({
            agentId: agent.id,
            chat: {
              ...chat,
              agentType: agent.type,
              isLoadingResponse: !!chat.hasStream,
              isReadOnly,
            },
            chatInstanceId,
          }),
        );
        dispatch(setActiveChat({ agentId: agent.id, chatId, chatInstanceId }));

        if (chat.hasStream) {
          const isSubscribed = await subscribeToChat(chatId, {
            onMessage: (chatId, chunk, isFirstContentChunk) =>
              handleMessageChunk(chatId, chunk, isFirstContentChunk, agent.id),
            onComplete: (chatId) => handleStreamComplete(chatId, agent.id),
            onError: (chatId) => handleStreamError(chatId, agent.id, agent.type),
          });
          if (!isSubscribed) {
            handleStreamError(chatId, agent.id, agent.type);
          }
        }
      } catch (e) {
        telemetry.captureError(e);
        navigateToAgentOrChat({ agentId: agent.id });
      }
    },
    [
      activeAgentId,
      chatsDetails,
      setActiveAgent,
      navigateToAgentOrChat,
      dispatch,
      chatInstanceId,
      activeChatId,
      get_chat,
      chatsList?.data,
      subscribeToChat,
      handleMessageChunk,
      handleStreamComplete,
      handleStreamError,
    ],
  );

  const closeSecondaryViews = useCallback(() => {
    dispatch(setArtifactsViewActive(false));
    closeIssuesPanel();
  }, [dispatch, closeIssuesPanel]);

  const setActiveChatClickHandler = useCallback(
    async (chatId: string) => {
      if (!activeAgentId || !activeAgent) {
        return;
      }
      closeSecondaryViews();
      if (isPageView) {
        navigateToAgentOrChat({ agentId: activeAgentId, chatId });
      } else {
        await setAndGetActiveChat({ agent: activeAgent, chatId });
      }
    },
    [
      activeAgent,
      activeAgentId,
      closeSecondaryViews,
      isPageView,
      navigateToAgentOrChat,
      setAndGetActiveChat,
    ],
  );

  const setAgentClickHandler = useCallback(
    async ({ agent }: { agent: AiAgent }) => {
      closeSecondaryViews();
      if (isPageView) {
        navigateToAgentOrChat({ agentId: agent.id });
      } else {
        setActiveAgent(agent);
      }
    },
    [closeSecondaryViews, isPageView, navigateToAgentOrChat, setActiveAgent],
  );

  const clearActiveAgent = useCallback(() => {
    dispatch(resetActiveAgentAction());
    closeSecondaryViews();
    dispatch(resetActiveFakeAgent());
  }, [dispatch, closeSecondaryViews]);

  const clearActiveAgentClickHandler = useCallback(() => {
    closeSecondaryViews();
    if (isPageView) {
      navigateToAgentOrChat({});
    } else {
      clearActiveAgent();
    }
  }, [isPageView, navigateToAgentOrChat, clearActiveAgent, closeSecondaryViews]);

  const createGhostChat = useCallback(
    ({ agentId, agentType }: { agentId: string; agentType: AgentType }) => {
      dispatch(createGhostChatAction({ agentId, agentType, chatInstanceId }));
    },
    [dispatch, chatInstanceId],
  );

  const startNewChat = useCallback(
    async ({
      agentId,
      agentType,
      biEventProps,
    }: {
      agentId: string;
      agentType: AgentType;
      biEventProps: Record<string, any>;
    }) => {
      sendBiEvent({
        name: AI_AGENTS_EVENTS.newChatClick,
        props: { ...biEventProps, agentType },
      });
      closeSecondaryViews();
      if (isPageView) {
        navigateToAgentOrChat({ agentId });
      } else {
        createGhostChat({ agentId, agentType });
      }
    },
    [sendBiEvent, closeSecondaryViews, isPageView, navigateToAgentOrChat, createGhostChat],
  );

  const renameChat = useCallback(
    async ({ chatId, name }: { chatId: string; name: string }) => {
      if (activeAgentId) {
        try {
          dispatch(setLoadingChatListItem({ agentId: activeAgentId, chatId }));
          await rename_chat(chatId, { name });
          dispatch(setChatName({ agentId: activeAgentId, chatId, name, chatInstanceId }));
        } catch (e) {
          telemetry.captureError(e);
        }
      }
    },
    [activeAgentId, dispatch, rename_chat, chatInstanceId],
  );

  const deleteChat = useCallback(
    async (chatId: string) => {
      if (activeAgentId) {
        try {
          dispatch(setLoadingChatListItem({ agentId: activeAgentId, chatId }));
          await delete_chat(chatId);
          dispatch(deleteChatAction({ agentId: activeAgentId, chatId, chatInstanceId }));
        } catch (e) {
          telemetry.captureError(e);
        }
      }
    },
    [activeAgentId, chatInstanceId, delete_chat, dispatch],
  );

  const togglePin = useCallback(
    async (chatId: string) => {
      const relevantChat = chatsList?.data?.find((chat) => chat.id === chatId);
      if (!!activeAgentId && !!relevantChat) {
        try {
          const isFavorite = !relevantChat.isFavorite;
          if (isFavorite) {
            sendBiEvent({ name: AI_AGENTS_EVENTS.starChat });
          }
          set_isFavorite_chat(chatId, { isFavorite });
          dispatch(setChatIsFavorite({ agentId: activeAgentId, chatId, isFavorite }));
        } catch (e) {
          telemetry.captureError(e);
        }
      }
    },
    [chatsList?.data, activeAgentId, set_isFavorite_chat, dispatch, sendBiEvent],
  );

  const getSearchResults = useCallback(
    async (localSearchText: string) => {
      if (activeAgentId && activeAgentType) {
        try {
          const trimmedSearch = localSearchText.trim();
          dispatch(setLoadingSearch({ agentId: activeAgentId, searchText: trimmedSearch }));
          const chats = await get_search({ searchText: localSearchText, agentId: activeAgentId });
          const chatsWithAgentType = chats?.map((chat) => ({
            ...chat,
            agentType: activeAgentType,
            isLoading: false,
          }));
          dispatch(setSearchResult({ agentId: activeAgentId, chats: chatsWithAgentType }));
        } catch (e) {
          telemetry.captureError(e);
        }
      }
    },
    [activeAgentId, dispatch, get_search, activeAgentType],
  );

  const clearSearchResults = useCallback(() => {
    if (activeAgentId) {
      dispatch(clearSearch({ agentId: activeAgentId }));
    }
  }, [activeAgentId, dispatch]);

  const inputPlaceholder = useMemo(() => {
    const defaultText = 'Ask anything';
    if (!activeAgent || !activeAgentType) {
      return defaultText;
    }
    switch (activeAgentType) {
      case AgentType.Documentation:
      case AgentType.Permissions:
        return defaultText;
      default:
        return `Message ${activeAgent.name} agent`;
    }
  }, [activeAgent, activeAgentType]);

  const handleAnimationProgressPersistence = useCallback(
    (progress: { messageCharIndex: number; reasoningChunkIndex: number }) => {
      if (activeAgentId && activeChatId) {
        dispatch(
          setMessageAnimationProgress({
            agentId: activeAgentId,
            chatId: activeChatId,
            progress,
          }),
        );
      }
    },
    [activeAgentId, activeChatId, dispatch],
  );

  const stopResponse = useCallback(
    async (chatId?: string) => {
      if (!activeAgentId) {
        return;
      }
      const _chatId = chatId ?? activeChatId;
      if (!_chatId) {
        return;
      }
      try {
        await delete_stream(_chatId);
        unsubscribeFromChat(_chatId);
        dispatch(stopResponseAction({ agentId: activeAgentId, chatId: _chatId, chatInstanceId }));
      } catch (e) {
        telemetry.captureError(e);
      }
    },
    [activeAgentId, activeChatId, chatInstanceId, delete_stream, unsubscribeFromChat, dispatch],
  );

  // Advanced mode logic
  const advancedMode = useSelector(selectAdvancedMode);
  const selectedAdditionalOrgIds = useSelector(selectSelectedAdditionalOrgIds);
  const connectedOrgs = useSelector(selectConnectedCrmOrgs);
  const { documentationAgentAdvancedMode } = useFeatureToggle();

  const handleModeChange = useCallback(
    (mode: AgentInputMode) => {
      dispatch(setAdvancedMode({ advancedMode: mode }));
      // When switching to multi-org mode, auto-select the first available org
      if (mode === AgentInputMode.MultiOrgMode) {
        const availableOrgs = connectedOrgs.filter((org) => org.id !== crmOrgId);
        if (availableOrgs.length > 0) {
          dispatch(setSelectedAdditionalOrgIds({ orgIds: [availableOrgs[0].id] }));
        }
      }
    },
    [dispatch, connectedOrgs, crmOrgId],
  );

  const isDocumentationAgent = activeAgentType === AgentType.Documentation;
  const enableAdvancedMode = documentationAgentAdvancedMode && isDocumentationAgent;

  return {
    chats: orderedChats,
    activeChat,
    activeAgent,
    fetchChats,
    onConfirmMessage,
    renameChat,
    deleteChat,
    togglePin,
    getSearchResults,
    clearSearchResults,
    inputPlaceholder,
    startNewChat,
    setAgentClickHandler,
    setActiveAgent,
    clearActiveAgentClickHandler,
    clearActiveAgent,
    setActiveChatClickHandler,
    setAndGetActiveChat,
    createGhostChat,
    setTempChatAsActive,
    handleAnimationProgressPersistence,
    stopResponse,
    advancedMode,
    handleModeChange,
    enableAdvancedMode,
    selectedAdditionalOrgIds,
    navigateToAgentOrChat,
  };
};

export default useAgentsChats;
